import { useEffect, useState } from 'react';
import { Input, Typography, Button, Space } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import styles from './JsonInput.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

type JsonInputProps = {
  value: Record<string, unknown> | null;
  onChange: (value: Record<string, unknown>) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  className?: string;
  showButtonsInline?: boolean;
  onCancel?: () => void;
  renderButtons?: (params: {
    hasChanges: boolean;
    error: string | null;
    onSave: () => void;
    onCancel: () => void;
  }) => React.ReactNode;
};

const JsonInput = ({
  value,
  onChange,
  placeholder,
  minRows = 5,
  maxRows = 20,
  className,
  showButtonsInline = true,
  onCancel: onCancelProp,
  renderButtons,
}: JsonInputProps) => {
  const { _ } = useLingui();
  const [textValue, setTextValue] = useState(
    value ? JSON.stringify(value, null, 2) : '{}'
  );
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValue, setOriginalValue] = useState(
    value ? JSON.stringify(value, null, 2) : '{}'
  );

  // Sync local state with external value changes
  useEffect(() => {
    const newValue = value ? JSON.stringify(value, null, 2) : '{}';
    setTextValue(newValue);
    setOriginalValue(newValue);
    setHasChanges(false);
    setError(null);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    setHasChanges(newValue !== originalValue);

    // Validate JSON immediately for user feedback
    try {
      JSON.parse(newValue);
      setError(null);
    } catch (err) {
      setError(_(msg`Invalid JSON format`));
    }
  };

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(textValue);
      setError(null);
      await onChange(parsed);
      setOriginalValue(textValue);
      setHasChanges(false);
    } catch (err) {
      setError(_(msg`Invalid JSON format - cannot save`));
    }
  };

  const handleCancel = () => {
    setTextValue(originalValue);
    setHasChanges(false);
    setError(null);
    onCancelProp?.();
  };

  return (
    <div>
      {renderButtons && renderButtons({ hasChanges, error, onSave: handleSave, onCancel: handleCancel })}
      <TextArea
        value={textValue}
        onChange={handleChange}
        placeholder={placeholder || _(msg`Enter JSON data...`)}
        autoSize={{ minRows, maxRows }}
        className={`${styles.textArea} ${className || ''}`}
        status={error ? 'error' : undefined}
      />
      {error && (
        <Text type="danger" className={styles.errorText}>
          {error}
        </Text>
      )}
      {showButtonsInline && hasChanges && !renderButtons && (
        <Space className={styles.buttonContainer}>
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!!error}
          >
            <Trans>Save</Trans>
          </Button>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={handleCancel}
          >
            <Trans>Cancel</Trans>
          </Button>
        </Space>
      )}
    </div>
  );
};

export default JsonInput;
